import React, { useState, useEffect } from "react";
import AppLayout from "../../components/AppLayout";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Trash2, AlertCircle, GitMerge, Archive, RotateCcw, 
  CheckCircle, XCircle, Users, Search, Filter, ArrowRight,
  RefreshCw, FileQuestion, TestTube, Copy
} from "lucide-react";

export default function CRMCleanup() {
  const [activeTab, setActiveTab] = useState("junk"); // junk, test, duplicates
  const [loading, setLoading] = useState(true);
  const [scanData, setScanData] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [archiveReason, setArchiveReason] = useState("");
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [showMigrationModal, setShowMigrationModal] = useState(false);

  useEffect(() => {
    loadScanData();
  }, []);

  const loadScanData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/admin/crm/cleanup/scan");
      setScanData(res.data);
    } catch (err) {
      toast.error("Failed to load cleanup scan");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrationPreview = async () => {
    try {
      const res = await api.post("/api/admin/crm/cleanup/migrate-types", {
        dry_run: true
      });
      setMigrationPreview(res.data);
      setShowMigrationModal(true);
    } catch (err) {
      toast.error("Failed to generate migration preview");
    }
  };

  const handleMigrationConfirm = async () => {
    try {
      const res = await api.post("/api/admin/crm/cleanup/migrate-types", {
        dry_run: false
      });
      toast.success(`Migrated ${res.data.changes} contact types`);
      setShowMigrationModal(false);
      loadScanData();
    } catch (err) {
      toast.error("Migration failed");
    }
  };

  const handleBulkArchive = async () => {
    if (selectedContacts.length === 0) {
      toast.error("No contacts selected");
      return;
    }

    if (!archiveReason.trim()) {
      toast.error("Please provide an archive reason");
      return;
    }

    if (!window.confirm(`Archive ${selectedContacts.length} contact(s)? This action can be reversed later.`)) {
      return;
    }

    try {
      await api.post("/api/admin/crm/cleanup/archive-bulk", {
        contact_ids: selectedContacts,
        reason: archiveReason
      });
      toast.success(`Archived ${selectedContacts.length} contact(s)`);
      setSelectedContacts([]);
      setArchiveReason("");
      loadScanData();
    } catch (err) {
      toast.error("Bulk archive failed");
    }
  };

  const handleMerge = async () => {
    if (!mergeSource || !mergeTarget) {
      toast.error("Please select both source and target contacts");
      return;
    }

    if (!window.confirm(`Merge ${mergeSource.stage_name} into ${mergeTarget.stage_name}? The source will be archived.`)) {
      return;
    }

    try {
      await api.post("/api/admin/crm/cleanup/merge", {
        source_id: mergeSource.id,
        target_id: mergeTarget.id,
        keep_fields: {
          stage_name: "target",
          email: "target",
          phone: "source",
          notes: "combine",
          shoots: "combine",
          shows: "combine"
        }
      });
      toast.success("Contacts merged successfully");
      setShowMergeModal(false);
      setMergeSource(null);
      setMergeTarget(null);
      loadScanData();
    } catch (err) {
      toast.error("Merge failed");
    }
  };

  const toggleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const tabs = [
    { id: "junk", label: "Junk", icon: Trash2, count: scanData?.junk_contacts?.length || 0 },
    { id: "test", label: "Test Data", icon: TestTube, count: scanData?.test_contacts?.length || 0 },
    { id: "duplicates", label: "Duplicates", icon: Copy, count: scanData?.potential_duplicates?.length || 0 }
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="animate-spin text-purple-500" size={32} />
        </div>
      </AppLayout>
    );
  }

  const currentData = activeTab === "junk" 
    ? scanData?.junk_contacts 
    : activeTab === "test"
    ? scanData?.test_contacts
    : scanData?.potential_duplicates;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            🧹 CRM Cleanup
          </h1>
          <p className="text-zinc-400">
            Clean up junk, test data, and merge duplicate contacts
          </p>
        </div>

        {/* Action Bar */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={loadScanData}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
            >
              <RefreshCw size={16} />
              Refresh Scan
            </button>

            <button
              onClick={handleMigrationPreview}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <RotateCcw size={16} />
              Migrate Types
            </button>
          </div>

          {selectedContacts.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-400">
                {selectedContacts.length} selected
              </span>
              <input
                type="text"
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                placeholder="Archive reason..."
                className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={handleBulkArchive}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
              >
                <Archive size={16} />
                Archive Selected
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? "text-purple-400 border-b-2 border-purple-500"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                <Icon size={18} />
                {tab.label}
                <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  activeTab === tab.id
                    ? "bg-purple-500/20 text-purple-300"
                    : "bg-zinc-800 text-zinc-400"
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="space-y-4">
          {currentData && currentData.length === 0 ? (
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">All Clean!</h3>
              <p className="text-zinc-400">No {activeTab} contacts found</p>
            </div>
          ) : (
            currentData?.map((contact, idx) => (
              <div
                key={contact.id || idx}
                className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Checkbox */}
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact.id)}
                    onChange={() => toggleSelectContact(contact.id)}
                    className="mt-1 w-5 h-5 rounded border-zinc-700 bg-zinc-800 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
                  />

                  {/* Contact Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          {contact.stage_name || contact.name || "Unknown"}
                        </h3>
                        <p className="text-sm text-zinc-400">
                          {contact.type && (
                            <span className="inline-block px-2 py-0.5 bg-zinc-800 rounded text-xs mr-2">
                              {contact.type}
                            </span>
                          )}
                          {contact.email || "No email"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {activeTab === "duplicates" && contact.possible_duplicate_of && (
                          <button
                            onClick={() => {
                              setMergeSource(contact);
                              // Find the duplicate target
                              const target = scanData.potential_duplicates.find(
                                c => c.id === contact.possible_duplicate_of
                              );
                              setMergeTarget(target);
                              setShowMergeModal(true);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                          >
                            <GitMerge size={14} />
                            Merge
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedContacts([contact.id]);
                            setArchiveReason(
                              activeTab === "junk" 
                                ? "Junk contact" 
                                : activeTab === "test"
                                ? "Test data"
                                : "Duplicate"
                            );
                          }}
                          className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                        >
                          <Archive size={14} />
                          Archive
                        </button>
                      </div>
                    </div>

                    {/* Reason */}
                    {contact.reason && (
                      <div className="flex items-start gap-2 mt-2 text-sm text-zinc-400">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{contact.reason}</span>
                      </div>
                    )}

                    {/* Duplicate Info */}
                    {activeTab === "duplicates" && contact.match_type && (
                      <div className="mt-2 text-xs text-zinc-500">
                        Match: {contact.match_type}
                        {contact.possible_duplicate_of && (
                          <span className="ml-2">
                            (Similar to ID: {contact.possible_duplicate_of})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Type Migration Modal */}
        {showMigrationModal && migrationPreview && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-zinc-800">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Type Migration Preview
                </h2>
                <p className="text-zinc-400">
                  {migrationPreview.changes} type(s) will be normalized
                </p>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-3">
                {migrationPreview.change_list?.length > 0 ? (
                  migrationPreview.change_list.map((change, idx) => (
                    <div
                      key={idx}
                      className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{change.name}</p>
                          <p className="text-sm text-zinc-400">{change.contact_id}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="px-3 py-1 bg-red-600/20 text-red-400 text-sm rounded">
                            {change.old_type}
                          </span>
                          <ArrowRight size={16} className="text-zinc-500" />
                          <span className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded">
                            {change.new_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-400">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    No migrations needed - all types are normalized
                  </div>
                )}
              </div>

              <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
                <button
                  onClick={() => setShowMigrationModal(false)}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                {migrationPreview.changes > 0 && (
                  <button
                    onClick={handleMigrationConfirm}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                  >
                    Confirm Migration
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Merge Modal */}
        {showMergeModal && mergeSource && mergeTarget && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-4xl w-full">
              <div className="p-6 border-b border-zinc-800">
                <h2 className="text-2xl font-bold text-white mb-2">
                  Merge Contacts
                </h2>
                <p className="text-zinc-400">
                  Choose which fields to keep from each contact
                </p>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {/* Source */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="px-3 py-1 bg-red-600/20 text-red-400 text-sm rounded font-medium">
                        SOURCE (Will be archived)
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {mergeSource.stage_name}
                    </h3>
                    <p className="text-sm text-zinc-400">{mergeSource.email}</p>
                    <p className="text-sm text-zinc-400">{mergeSource.phone}</p>
                    <p className="text-xs text-zinc-500 mt-2">Type: {mergeSource.type}</p>
                  </div>

                  {/* Target */}
                  <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="px-3 py-1 bg-green-600/20 text-green-400 text-sm rounded font-medium">
                        TARGET (Will be kept)
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">
                      {mergeTarget.stage_name}
                    </h3>
                    <p className="text-sm text-zinc-400">{mergeTarget.email}</p>
                    <p className="text-sm text-zinc-400">{mergeTarget.phone}</p>
                    <p className="text-xs text-zinc-500 mt-2">Type: {mergeTarget.type}</p>
                  </div>
                </div>

                <div className="mt-6 bg-blue-600/10 border border-blue-600/30 rounded-lg p-4">
                  <p className="text-sm text-blue-300">
                    <strong>Merge Strategy:</strong> Target contact will be kept. 
                    Phone from source, notes and activities will be combined.
                  </p>
                </div>
              </div>

              <div className="p-6 border-t border-zinc-800 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowMergeModal(false);
                    setMergeSource(null);
                    setMergeTarget(null);
                  }}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMerge}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Confirm Merge
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
